import { diff3Merge, diffPatch } from "node-diff3";
import type { IPatchRes } from "node-diff3";
import type { ConflictRegion, MergeResult } from "./types.js";

/**
 * Performs a 3-way merge using the diff3 algorithm.
 *
 * @param base - The common ancestor (pristine generated state user edited against)
 * @param ours - The new generated version
 * @param theirs - The user's customized version
 * @returns MergeResult with content, conflict flag, and conflict regions
 */
export function threeWayMerge(base: string, ours: string, theirs: string): MergeResult {
    const baseLines = base.split("\n");
    const oursLines = ours.split("\n");
    const theirsLines = theirs.split("\n");

    const regions = diff3Merge(oursLines, baseLines, theirsLines);

    const outputLines: string[] = [];
    const conflicts: ConflictRegion[] = [];
    let currentLine = 1;

    for (const region of regions) {
        if (region.ok) {
            outputLines.push(...region.ok);
            currentLine += region.ok.length;
        } else if (region.conflict) {
            // Attempt to resolve false conflicts caused by adjacent non-overlapping changes.
            // node-diff3 groups adjacent edits into one conflict region even when the
            // generator and user changed completely different lines within that region.
            const resolved = tryResolveConflict(
                region.conflict.a,  // ours (generator)
                region.conflict.o,  // base
                region.conflict.b   // theirs (user)
            );

            if (resolved !== null) {
                outputLines.push(...resolved);
                currentLine += resolved.length;
            } else {
                const startLine = currentLine;

                outputLines.push("<<<<<<< Generated");
                outputLines.push(...region.conflict.a);
                outputLines.push("=======");
                outputLines.push(...region.conflict.b);
                outputLines.push(">>>>>>> Your customization");

                // Total lines in conflict block: 3 markers + a.length + b.length
                // endLine is the line number of the ">>>>>>>" marker
                const conflictLines = region.conflict.a.length + region.conflict.b.length + 3;
                conflicts.push({
                    startLine,
                    endLine: startLine + conflictLines - 1,
                    ours: region.conflict.a,
                    theirs: region.conflict.b
                });

                currentLine += conflictLines;
            }
        }
    }

    const result: MergeResult = {
        content: outputLines.join("\n"),
        hasConflicts: conflicts.length > 0,
        conflicts
    };

    // Detect lines that were unchanged context in the customer's THEIRS but
    // got silently deleted by the generator. Only meaningful when no
    // conflicts surfaced — when conflicts exist, the customer already has a
    // marker to review. We compare against the lines diff3 actually emitted,
    // which means a deleted-once-but-still-present-elsewhere line counts as
    // dropped if its multiplicity decreased; that's accurate for the auth0
    // case (each wiring line is a unique signature).
    if (conflicts.length === 0) {
        const dropped = computeDroppedContextLines(baseLines, theirsLines, outputLines);
        if (dropped.length > 0) {
            result.droppedContextLines = dropped;
        }
    }

    return result;
}

/**
 * Lines present in BOTH base and theirs but absent from the merge output.
 * These are lines the customer's THEIRS treated as context (didn't edit);
 * OURS deleted; diff3 sided with the deletion. Returns empty array when
 * none. Order preserved per first occurrence in `base`.
 */
function computeDroppedContextLines(
    baseLines: string[],
    theirsLines: string[],
    mergedLines: string[]
): string[] {
    const baseCounts = countLines(baseLines);
    const theirsCounts = countLines(theirsLines);
    const mergedCounts = countLines(mergedLines);

    const dropped: string[] = [];
    const seen = new Set<string>();

    for (const line of baseLines) {
        if (seen.has(line)) continue;
        const inBase = baseCounts.get(line) ?? 0;
        const inTheirs = theirsCounts.get(line) ?? 0;
        const inMerged = mergedCounts.get(line) ?? 0;

        // Line was in BOTH base and theirs (unchanged context) AND its
        // multiplicity went down in the merge. The deletion came from OURS.
        if (inBase > 0 && inTheirs > 0 && inMerged < Math.min(inBase, inTheirs)) {
            dropped.push(line);
            seen.add(line);
        }
    }

    return dropped;
}

function countLines(lines: string[]): Map<string, number> {
    const counts = new Map<string, number>();
    for (const line of lines) {
        counts.set(line, (counts.get(line) ?? 0) + 1);
    }
    return counts;
}

/**
 * Attempts to resolve a false conflict by checking if ours and theirs
 * changed non-overlapping lines within the conflict region's base.
 * Returns merged lines if resolvable, or null for a real conflict.
 */
function tryResolveConflict(
    oursLines: string[],
    baseLines: string[],
    theirsLines: string[]
): string[] | null {
    if (baseLines.length === 0) {
        return null;
    }

    const oursPatches = diffPatch(baseLines, oursLines);
    const theirsPatches = diffPatch(baseLines, theirsLines);

    if (oursPatches.length === 0) return theirsLines;
    if (theirsPatches.length === 0) return oursLines;

    if (patchesOverlap(oursPatches, theirsPatches)) {
        return null;
    }

    return applyBothPatches(baseLines, oursPatches, theirsPatches);
}

/**
 * Checks if any patch ranges overlap in base coordinates.
 */
function patchesOverlap(
    oursPatches: IPatchRes<string>[],
    theirsPatches: IPatchRes<string>[]
): boolean {
    for (const op of oursPatches) {
        const oStart = op.buffer1.offset;
        const oEnd = oStart + op.buffer1.length;
        for (const tp of theirsPatches) {
            const tStart = tp.buffer1.offset;
            const tEnd = tStart + tp.buffer1.length;

            // Both are pure insertions at the same point
            if (op.buffer1.length === 0 && tp.buffer1.length === 0 && oStart === tStart) {
                return true;
            }
            // Insertion at the exact boundary of a deletion/replacement.
            // Example: generator deletes lines [0,2) and user inserts at offset 2.
            // The insertion's surrounding context has been rewritten, so splicing
            // both into base would produce a semantically broken result (e.g. user
            // content appended after a file-deletion). Treating this as overlap is
            // conservative — it keeps the conflict for manual resolution rather than
            // silently producing a bad merge.
            if (tp.buffer1.length === 0 && tStart === oEnd) {
                return true;
            }
            if (op.buffer1.length === 0 && oStart === tEnd) {
                return true;
            }
            // Standard range overlap
            if (oStart < tEnd && tStart < oEnd) {
                return true;
            }
        }
    }
    return false;
}

/**
 * Applies both sets of non-overlapping patches to base.
 * Patches are sorted by descending offset and applied via splice
 * to avoid index invalidation.
 */
function applyBothPatches(
    baseLines: string[],
    oursPatches: IPatchRes<string>[],
    theirsPatches: IPatchRes<string>[]
): string[] {
    const allPatches = [
        ...oursPatches.map(p => ({
            offset: p.buffer1.offset,
            length: p.buffer1.length,
            replacement: p.buffer2.chunk,
        })),
        ...theirsPatches.map(p => ({
            offset: p.buffer1.offset,
            length: p.buffer1.length,
            replacement: p.buffer2.chunk,
        })),
    ];

    allPatches.sort((a, b) => b.offset - a.offset);

    const result = [...baseLines];
    for (const p of allPatches) {
        result.splice(p.offset, p.length, ...p.replacement);
    }
    return result;
}
